/**
 * IDT 看板「デザイン評価」カードの radar 横に置くスコア表・分類凡例。
 *
 * DesignEvaluationRadar から分離した表示専用パーツ群 (貢献度スコア表 / 解像度スコア表 /
 * 解像度の分類凡例)。karte モーダルと看板の双方で radar の左右に絶対配置する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイン評価 radar の補助表示 (スコア表・分類凡例)
 * @graph-connects none
 */
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CATEGORY_COLORS,
  EVALUATION_AXES,
  EVALUATION_RADAR_COLOR,
  RESOLUTION_AXES,
  RESOLUTION_RADAR_COLOR,
  type EvalSeriesPoint,
} from './designEvaluation.js';

/**
 * チーム貢献度評価の各軸スコア (橙) と評価対象件数 (灰) を表で表示する。
 * 列見出し「評価対象/件」の下に横罫線、行見出し (軸名) の右に縦罫線を引いて表を整える。
 * @graph-connects none
 */
export function EvaluationScorePanel({ selected }: { selected: EvalSeriesPoint }) {
  const { t } = useTranslation();
  return (
    // 行間 gap を取らず py で空け、行見出し列の border-r と見出し行の border-b を連続させて
    // 十字に交差させる (縦罫線は途切れない 1 本)。
    <div className="grid grid-cols-[auto_auto_auto] items-baseline text-right">
      {/* 見出し行: col1 (空白) が十字の交点。border-r=縦罫線 / border-b=横罫線 */}
      <div className="border-b border-r border-[var(--color-border)] pb-2 pr-3 text-xs leading-none">
        &nbsp;
      </div>
      <div className="col-span-2 border-b border-[var(--color-border)] pb-2 pl-3 text-xs leading-none text-[var(--color-text-secondary)]">
        {t('idtKpi.designEvaluation.evalCountHeader')}
      </div>
      {EVALUATION_AXES.map((a) => {
        const score = selected.evaluation[a.key];
        const count = selected.evaluationCounts[a.key];
        return (
          <Fragment key={a.key}>
            <div className="border-r border-[var(--color-border)] py-2.5 pr-3 text-left text-[13px] leading-none text-[var(--color-text-secondary)]">
              {t(a.labelKey)}
            </div>
            <div
              className="py-2.5 pl-3 font-mono text-lg font-bold leading-none tabular-nums"
              style={{ color: EVALUATION_RADAR_COLOR }}
            >
              {score == null ? '—' : score.toFixed(2)}
            </div>
            <div className="py-2.5 pl-6 font-mono text-lg font-bold leading-none tabular-nums text-[var(--color-text-secondary)]">
              {count ?? '—'}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

/**
 * PJ解像度 5 軸のスコア (青緑) を radar 左側に「評価表の鏡像」として表で表示する。
 * 点数列を外側 (左)・軸名列を radar 寄り (右) に置き、軸名列の左に縦罫線・見出し「点数」の下に
 * 横罫線を引いて十字に交差させる。軸名は【】を外し 2 行に折り返す。
 * @graph-connects none
 */
export function ResolutionScorePanel({ selected }: { selected: EvalSeriesPoint }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-[auto_auto] items-baseline text-right">
      {/* 見出し行: col1 軸名 (空白, border-r + border-b = 十字の交点) / col2「点数」(border-b)。
          軸名は左に少し余白、点数との間も広めに取る (右表の margin に合わせる)。 */}
      <div className="border-b border-r border-[var(--color-border)] pb-2 pl-2 pr-3 text-xs leading-none">
        &nbsp;
      </div>
      <div className="border-b border-[var(--color-border)] pb-2 pl-6 text-xs leading-none text-[var(--color-text-secondary)]">
        {t('idtKpi.designEvaluation.scoreHeader')}
      </div>
      {RESOLUTION_AXES.map((a) => {
        const value = selected.resolution[a.key];
        // 軸名は分類の代表色 (企画=青 / 設計=桃) で表示する
        const labelColor = a.category ? CATEGORY_COLORS[a.category] : 'var(--color-text-secondary)';
        return (
          <Fragment key={a.key}>
            <div
              className="border-r border-[var(--color-border)] py-2.5 pl-2 pr-3 text-left text-[13px] leading-none"
              style={{ color: labelColor }}
            >
              {t(`idtKpi.designEvaluation.shortAxes.${a.key}`)}
            </div>
            <div
              className="py-2.5 pl-6 font-mono text-lg font-bold leading-none tabular-nums"
              style={{ color: RESOLUTION_RADAR_COLOR }}
            >
              {value == null ? '—' : value.toFixed(2)}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

/** 解像度軸の分類凡例 (企画=青 / 設計=桃)。横並びで radar 直下に置く。 @graph-connects none */
export function CategoryLegend() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-4 text-xs text-[var(--color-text-muted)]">
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block w-2.5 h-2.5 rounded-sm"
          style={{ background: CATEGORY_COLORS.plan }}
          aria-hidden="true"
        />
        {t('idtKpi.designEvaluation.category.plan')}
      </div>
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block w-2.5 h-2.5 rounded-sm"
          style={{ background: CATEGORY_COLORS.design }}
          aria-hidden="true"
        />
        {t('idtKpi.designEvaluation.category.design')}
      </div>
    </div>
  );
}
